import { goalExplosionSelectionKey, type GoalExplosionSelection } from '../game/effects/GoalExplosionSelection';
import { MAX_ROOM_CONTROL_BYTES, type RoomWireEvent } from './roomProtocol';

export const MAX_ROOM_ASSEMBLED_CONTROL_BYTES = 1024 * 1024;
const FRAGMENT_CHARACTERS = 8 * 1024;
const MAX_FRAGMENTS = 128;
const encoder = new TextEncoder();

/** Frames stay bounded; ordered WebSocket delivery keeps one assembly in flight. */
export function encodeRoomEventFrames(event: RoomWireEvent): readonly ArrayBuffer[] {
  const json = JSON.stringify(compactEvent(event));
  const bytes = encoder.encode(json);
  if (bytes.byteLength > MAX_ROOM_ASSEMBLED_CONTROL_BYTES) throw new Error('Room control state exceeds its budget.');
  if (bytes.byteLength <= MAX_ROOM_CONTROL_BYTES) return [bytes.buffer as ArrayBuffer];
  const count = Math.ceil(json.length / FRAGMENT_CHARACTERS);
  if (count > MAX_FRAGMENTS) throw new Error('Room control state exceeds its fragment budget.');
  return Array.from({ length: count }, (_, index) => encoder.encode(JSON.stringify({
    type: 'control-fragment', index, count, chunk: json.slice(index * FRAGMENT_CHARACTERS, (index + 1) * FRAGMENT_CHARACTERS),
  })).buffer as ArrayBuffer);
}

export class RoomControlAssembler {
  #parts: string[] = [];
  #count = 0;
  #bytes = 0;

  accept(value: Record<string, unknown>): Record<string, unknown> | null {
    if (value.type !== 'control-fragment') {
      if (this.#count) throw new Error('Room control fragments were interrupted.');
      return expandEvent(value);
    }
    const { index, count, chunk } = value;
    if (!Number.isSafeInteger(index) || !Number.isSafeInteger(count) || typeof chunk !== 'string'
      || chunk.length === 0 || chunk.length > FRAGMENT_CHARACTERS || (count as number) < 2 || (count as number) > MAX_FRAGMENTS
      || index !== this.#parts.length || this.#count !== 0 && count !== this.#count) {
      throw new Error('Invalid room control fragment sequence.');
    }
    this.#count = count as number;
    this.#bytes += encoder.encode(chunk).byteLength;
    if (this.#bytes > MAX_ROOM_ASSEMBLED_CONTROL_BYTES) throw new Error('Room control assembly exceeds its budget.');
    this.#parts.push(chunk);
    if (this.#parts.length !== this.#count) return null;
    const json = this.#parts.join('');
    this.reset();
    const parsed: unknown = JSON.parse(json);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)
      || (parsed as Record<string, unknown>).type === 'control-fragment') throw new Error('Invalid assembled room control.');
    return expandEvent(parsed as Record<string, unknown>);
  }

  get pending(): boolean { return this.#count !== 0; }

  reset(): void { this.#parts = []; this.#count = 0; this.#bytes = 0; }
}

function compactEvent(event: RoomWireEvent): object {
  if (event.type !== 'explosion-state') return event;
  const definitions: GoalExplosionSelection[] = [];
  const indices = new Map<string, number>();
  const reference = (selection: GoalExplosionSelection | null): number | null => {
    if (selection === null) return null;
    const key = goalExplosionSelectionKey(selection);
    const known = indices.get(key);
    if (known !== undefined) return known;
    const index = definitions.length;
    definitions.push(selection);
    indices.set(key, index);
    return index;
  };
  const entities = event.entities.map(entity => ({ entityId: entity.entityId,
    current: reference(entity.current), next: reference(entity.next) }));
  const goal = event.goal ? { ...event.goal, selection: reference(event.goal.selection) } : null;
  return { type: event.type, definitions, entities, goal };
}

function expandEvent(value: Record<string, unknown>): Record<string, unknown> {
  if (value.type !== 'explosion-state') return value;
  const definitions = value.definitions;
  if (!Array.isArray(definitions) || definitions.length > 13
    || !Array.isArray(value.entities) || value.entities.length > 6) throw new Error('Invalid explosion definition table.');
  const selection = (index: unknown): unknown => {
    if (index === null) return null;
    if (!Number.isSafeInteger(index) || (index as number) < 0 || (index as number) >= definitions.length) {
      throw new Error('Invalid explosion definition reference.');
    }
    return definitions[index as number];
  };
  const entities = value.entities.map((entity: Record<string, unknown>) => ({
    entityId: entity.entityId, current: selection(entity.current), next: selection(entity.next),
  }));
  const goal = value.goal === null ? null : value.goal as Record<string, unknown>;
  return { type: value.type, entities, goal: goal === null ? null : { goalTick: goal.goalTick,
    scorerEntityId: goal.scorerEntityId, selection: selection(goal.selection) } };
}
